import React from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { ExclamationCircle, Trash } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { FAILED_DOMAIN_STATUSES } from "../../constants";
import { getDomainStatus } from "../../helpers";
import { whiteLabelStep } from "../../qraphql/globalVar/state";
import {
  useCreateOrUpdateWhiteLabelDomainMutation,
  useOrganizerWhiteLabelQuery,
  useRetryWhiteLabelDomainSetupMutation,
} from "../../qraphql/whiteLabel/whiteLabel.hooks";
import { DomainType, WhiteLaelStepEnum } from "../../types";

interface IDomainStatusProps {
  refetchWhiteLabelInfo: () => void;
  isSubdomainMode?: boolean;
}

export default function DomainStatus({
  refetchWhiteLabelInfo,
  isSubdomainMode = false,
}: IDomainStatusProps) {
  const { t } = useTranslation();
  const { data: whiteLabelInfo } = useOrganizerWhiteLabelQuery();
  const { className, text } = getDomainStatus(
    whiteLabelInfo.organizer?.whiteLabel?.domainStatus
  );

  const [
    createOrUpdateWhiteLabelDomain,
    { loading: createOrUpdateWhiteLabelDomainLoading },
  ] = useCreateOrUpdateWhiteLabelDomainMutation();
  const [
    retryWhiteLabelDomainSetup,
    { loading: retryWhiteLabelDomainSetupLoading },
  ] = useRetryWhiteLabelDomainSetupMutation();

  const handleDomainDelete = () => {
    createOrUpdateWhiteLabelDomain({
      variables: {
        domainType: isSubdomainMode ? DomainType.Subdomain : DomainType.Domain,
        whiteLabelId: whiteLabelInfo.organizer?.whiteLabel?.id,
        domain: null,
      },
      onCompleted: () => {
        refetchWhiteLabelInfo();
        whiteLabelStep(WhiteLaelStepEnum.MediaDesign);
      },
    });
  };

  const handleTrySetup = () => {
    if (retryWhiteLabelDomainSetupLoading) return;
    retryWhiteLabelDomainSetup({
      variables: { whiteLabelId: whiteLabelInfo.organizer?.whiteLabel?.id },
      onCompleted: () => {
        refetchWhiteLabelInfo();
      },
    });
  };

  return (
    <div className="ms-md-4 mt-3">
      <Row className="text-start align-items-center ms-md-1">
        <Col xs={5} md={3}>
          <div className="text-truncate">
            <div className="fs-9 text-info">{t("whiteLabel.Domain")}</div>
            <div>{whiteLabelInfo.organizer?.whiteLabel?.domain}</div>
          </div>
        </Col>
        <Col xs={5} md={7}>
          <div className="fs-9 text-info">{t("whiteLabel.Status")}</div>
          <div className={className}>{text && t(`whiteLabel.${text}`)}</div>
        </Col>
        <Col xs="auto" className="ms-auto ps-0">
          {createOrUpdateWhiteLabelDomainLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <Trash
              className="fs-3 cursor-pointer"
              onClick={handleDomainDelete}
            />
          )}
        </Col>
      </Row>
      <hr className="mt-2 mb-0" />
      {FAILED_DOMAIN_STATUSES.includes(
        whiteLabelInfo.organizer?.whiteLabel?.domainStatus
      ) && (
        <div className="text-start text-danger mt-3 bg-danger-light p-3 rounded d-flex align-items-center gap-3">
          <div>
            <ExclamationCircle />
          </div>
          <div className="fs-7">
            <div>
              {t(
                "whiteLabel.We noticed that domain wasn't updated correctly. Please"
              )}{" "}
              <span
                className="text-danger text-decoration-underline cursor-pointer"
                onClick={handleTrySetup}
              >
                {t("whiteLabel.try setup again")}
              </span>
              {retryWhiteLabelDomainSetupLoading && (
                <Spinner animation="border" size="sm" className="ms-2" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
